<template>
  <main>
    <vf-notification
      v-if="notification.message"
      class="mb-4 px-6 container"
      :type="notification.type"
      @close="notification.message = ''"
    >
      <base-interpolator :content="notification.message">
        <template #contact="{ args: [message] }">
          <base-link class="underlined" target="_blank" to="/help/contact-us">
            {{ message }}
          </base-link>
        </template>
      </base-interpolator>
    </vf-notification>
    <slot name="language-switcher" />
    <div class="my-6 container" :class="brandClasses.formsWrapper">
      <div class="px-6 py-4 space-y-2" :class="brandClasses.trackOrderForm" data-test-id="form-order-status">
        <div v-if="overrideTitles" class="mb-2">
          {{ $t.guestUser }}
        </div>
        <h1 id="order-status-form" :class="brandClasses.heading">
          {{ $t.orderStatus }}
        </h1>
        <p class="mb-2 text-xs c-grey-20">
          {{ $t.requiredField }}
        </p>
        <base-form
          ref="formRef"
          aria-labelledby="order-status-form"
          class="space-y-4"
          :form
          :scroll-to-error="false"
          validate-on="submit"
          @reset="resetForm"
          @submit="submit"
        >
          <vf-form-field
            v-slot="{ attrs }"
            name="orderNumber"
            :rule="[
              validateRequired($t.orderNumber),
              validateOneOfLengths(orderNumberLengths, $t.orderNumberErrorLength),
            ]"
          >
            <vf-input v-model.uppercase="form.orderNumber" v-bind="attrs" :label="$t.orderNumber" required>
              <template #after>
                <vf-toggletip>
                  <template #trigger>
                    <span class="sr-only">{{ $t.orderNumber }}</span>
                    <vf-icon name="info" size="md" />
                  </template>
                  <div class="space-y-2">
                    <img v-if="showOrderNumberToolTipImage" alt="" :src="$t.orderNumberToolTipImage">
                    <div v-html="$t.orderNumberToolTip" />
                  </div>
                </vf-toggletip>
              </template>
            </vf-input>
          </vf-form-field>
          <vf-form-field
            v-slot="{ attrs }"
            :hint="$t.emailFormatHint"
            name="email"
            :rule="[validateRequired($t.email), validateEmail()]"
          >
            <input-email v-model="form.email" v-bind="attrs" />
          </vf-form-field>
          <vf-form-field
            v-if="showZipCodeInput"
            v-slot="{ attrs }"
            name="postalCode"
            :rule="[validateRequired($t.zipCode), ...getValidator('postalCode', $t.zipCode)]"
          >
            <input-postal-code
              v-model="postalCode"
              v-bind="attrs"
              :label="$t.orderStatusZipCode"
              required
            >
              <template v-if="$t.zipCodeTooltip" #after>
                <vf-toggletip placement="top-end">
                  <template #trigger>
                    <span class="sr-only">{{ $t.zipCode }}</span>
                    <vf-icon name="info" size="md" />
                  </template>
                  <span v-html="$t.zipCodeTooltip" />
                </vf-toggletip>
              </template>
            </input-postal-code>
          </vf-form-field>
          <span v-if="showZipCodeInput && showOrderStatusHelpText">{{ $t.orderStatusZipCodeText }}</span>
          <vf-button
            :class="brandClasses.trackOrderButton"
            data-test-id="form-order-status-check"
            :disabled="loading"
            :loading
            type="submit"
          >
            {{ $t.orderCheckStatus }}
          </vf-button>
        </base-form>
        <div v-if="showLoginCta" class="mt-4 flex center gap-2">
          {{ $t.alreadyAMember }}
          <vf-link @click="DialogSignIn.open({ formLocation: 'modal:single:order-status:none' })">
            {{ $t.signIn }}
          </vf-link>
        </div>
      </div>
      <div v-if="showLoginForm" class="px-6 py-4">
        <template v-if="$feature.allowPasswordLogin && overrideTitles">
          <div>
            {{ $t.haveAnAccount }}
          </div>
          <h2 :class="brandClasses.heading">
            {{ $t.signInToTrack }}
          </h2>
          <p class="text-xs c-grey-20">
            {{ $t.requiredField }}
          </p>
        </template>
        <form-sign-in
          v-if="$feature.allowPasswordLogin"
          :compact="overrideTitles"
          form-location="inline:body:order-status:none"
        />
        <form-simplified-enrollment
          v-else
          :compact="overrideTitles"
          entry-point="Track or Return"
          form-location="inline:body:order-status:none"
        >
          <template #lookup-heading v-if="overrideTitles">
            <div :class="brandClasses.simplifiedEnrollmentLookupHeading">
              {{ $t.haveAnAccount }}
            </div>
            <h2 :class="[brandClasses.heading, brandClasses.simplifiedEnrollmentLookupHeading]">
              {{ $t.signInToTrack }}
            </h2>
          </template>
        </form-simplified-enrollment>
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'

definePageMeta({
  middleware() {
    const auth = useAuthStore()
    if (auth.loggedIn) return '/account/order-history'
  }
})

const { getValidator } = useAddressApiRules()
const {
  brandClasses,
  orderStatusHelpText,
  orderNumberLengths,
  overrideTitles,
  showOrderNumberToolTipImage,
  showLoginCta,
  showLoginForm,
  showZipCodeInput
} = useAppConfig().pages.orderStatus
const auth = useAuthStore()
const { validateEmail, validateRequired } = useLocalizedValidators()
const { $t } = useNuxtApp()
const recaptcha = useRecaptcha()
const router = useRouter()
const { DialogSignIn } = useDialogsStore()

const showOrderStatusHelpText = orderStatusHelpText.requiredLocales.includes(useLocale())

const formRef = ref<InstanceType<typeof BaseFormType>>()
const initial = {
  orderNumber: '',
  email: '',
  postalCode: ''
}
const form = reactive(initial)
const resetForm = (initial) => Object.assign(form, initial)
const notification = reactive({
  message: '',
  type: 'error' as const
})
const loading = ref(false)
/**
 * transforms postalCode to uppercase to cover cases like CA postal codes to pass validation and be submitted as uppercase to BE (e.g. e5a 3n3)
 */
const postalCode = computed({
  get: () => form.postalCode,
  set: (value) => form.postalCode = value.toUpperCase()
})
async function submit() {
  loading.value = true
  notification.message = ''
  try {
    const captchaResponse = await recaptcha.execute('trackOrder')
    const orders: any[] = await useApi().consumer.$trackOrder({
      recaptcha_response: captchaResponse,
      orderNo: form.orderNumber,
      customerEmail: form.email,
      ...(showZipCodeInput && { zipCode: form.postalCode }),
    })

    const order = orders[0]

    const query = {
      orderNumber: form.orderNumber,
      email: form.email,
      ...(showZipCodeInput && { zipCode: form.postalCode }),
    }
    /**
     * The following code is a workaround that there is no easy solution from the api side.
     * If the user tries to search with their billing address or account email, the payload they receive comes from SFCC
     * If the user tries to search with their shipping address email the payload comes from the OMS
     *
     * The CORRECT payload comes from the OMS. The solution for now is check if the shipment email is different from the form, and then retrigger the backend payload
     *
     */
    if (order.shipments.length
      && !isArray(order.shipments[0])
      && order.shipments[0].shippingAddress.email !== form.email) {
      query.email = order.shipments[0].shippingAddress.email
    }

    router.push({
      path: '/order-details',
      query
    })
  }
  catch (err) {
    assertApiError(err)
    notification.message = err.response?.status === 400 ? $t.orderStatusNotFound : err.message
  }
  finally {
    formRef.value?.reset()
    loading.value = false
  }
}

const validateOneOfLengths = (_sizes: number[], message?: string): Validator =>
  (value) => !_sizes.includes(value?.length) && (message || $t.validators.exactLength.replace('{size}', `${_sizes.join(` ${$t.or} `)}`))

whenever(() => auth.loggedIn, () => {
  router.push('/account/order-history')
})
</script>
